import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Folder, File, FolderOpen, Plus, MoreVertical, Trash2 } from 'lucide-react';
import { WebContainerService, FileSystemNode } from '../services/webcontainer';

interface FilesExplorerProps {
  onFileSelect?: (filePath: string, content: string) => void;
  className?: string;
}

interface TreeNodeProps {
  node: FileSystemNode;
  level: number;
  onFileSelect?: (filePath: string, content: string) => void;
  onRefresh: () => void;
}

const TreeNode: React.FC<TreeNodeProps> = ({ node, level, onFileSelect, onRefresh }) => {
  const [isExpanded, setIsExpanded] = useState(level === 0);
  const [isLoading, setIsLoading] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [newItemType, setNewItemType] = useState<'file' | 'directory'>('file');
  const [loadedChildren, setLoadedChildren] = useState<FileSystemNode[] | undefined>(node.children);

  // Reset loaded children when node changes (during refresh)
  useEffect(() => {
    setLoadedChildren(node.children);
  }, [node.children]);

  const handleToggle = async () => {
    if (node.type === 'directory') {
      if (!isExpanded && !loadedChildren) {
        // Load directory contents when expanding for the first time
        setIsLoading(true);
        try {
          const webcontainer = WebContainerService.getInstance();
          const children = await webcontainer.readDirectory(node.path);
          setLoadedChildren(children);
        } catch (error) {
          console.error('Failed to read directory:', error);
        } finally {
          setIsLoading(false);
        }
      }
      setIsExpanded(!isExpanded);
    } else if (onFileSelect) {
      setIsLoading(true);
      try {
        const webcontainer = WebContainerService.getInstance();
        const content = await webcontainer.readFile(node.path);
        onFileSelect(node.path, content);
      } catch (error) {
        console.error('Failed to read file:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const handleCreateNew = async () => {
    if (!newItemName.trim()) return;
    
    setIsCreatingNew(false);
    setNewItemName('');
    
    try {
      const webcontainer = WebContainerService.getInstance();
      const newPath = `${node.path}/${newItemName}`;
      
      if (newItemType === 'file') {
        await webcontainer.writeFile(newPath, '');
      } else {
        await webcontainer.createDirectory(newPath);
      }
      
      onRefresh();
    } catch (error) {
      console.error('Failed to create new item:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const webcontainer = WebContainerService.getInstance();
      await webcontainer.deleteFile(node.path);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
    setShowContextMenu(false);
  };

  const getFileIcon = () => {
    if (node.type === 'directory') {
      return isExpanded ? <FolderOpen className="w-4 h-4 text-blue-500" /> : <Folder className="w-4 h-4 text-blue-500" />;
    }
    
    // File type icons based on extension
    const extension = node.name.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'js':
      case 'jsx':
        return <File className="w-4 h-4 text-yellow-500" />;
      case 'ts':
      case 'tsx':
        return <File className="w-4 h-4 text-blue-600" />;
      case 'json':
        return <File className="w-4 h-4 text-green-500" />;
      case 'html':
        return <File className="w-4 h-4 text-orange-500" />;
      case 'css':
        return <File className="w-4 h-4 text-purple-500" />;
      case 'md':
        return <File className="w-4 h-4 text-gray-500" />;
      default:
        return <File className="w-4 h-4 text-gray-400" />;
    }
  };

  const getChevronIcon = () => {
    if (node.type === 'file') return null;
    return isExpanded ? 
      <ChevronDown className="w-4 h-4 text-gray-500" /> : 
      <ChevronRight className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div data-tree-node>
      <div
        className={`flex items-center space-x-1 px-2 py-1 hover:bg-gray-100 rounded cursor-pointer transition-colors group ${
          isLoading ? 'opacity-50' : ''
        }`}
        style={{ paddingLeft: `${level * 16 + 8}px` }}
        onClick={handleToggle}
        onContextMenu={handleContextMenu}
      >
        {getChevronIcon()}
        {getFileIcon()}
        <span className="text-sm text-gray-700 truncate flex-1">{node.name}</span>
        {isLoading && <div className="ml-2 w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />}
        
        {node.type === 'directory' && isExpanded && !loadedChildren && isLoading && (
          <div className="ml-2 text-xs text-gray-500">Loading...</div>
        )}
        
        {node.type === 'directory' && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsCreatingNew(true);
              setNewItemType('file');
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
            title="Create new file"
          >
            <Plus size={12} className="text-gray-500" />
          </button>
        )}
        
        <button
          onClick={handleContextMenu}
          className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
          title="More options"
        >
          <MoreVertical size={12} className="text-gray-500" />
        </button>
      </div>
      
      {isCreatingNew && (
        <div className="px-2 py-1" style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}>
          <div className="flex items-center space-x-2 bg-blue-50 p-2 rounded border border-blue-200">
            <input
              type="text"
              value={newItemName}
              onChange={(e) => setNewItemName(e.target.value)}
              placeholder={`New ${newItemType} name`}
              className="flex-1 text-sm px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleCreateNew();
                } else if (e.key === 'Escape') {
                  setIsCreatingNew(false);
                  setNewItemName('');
                }
              }}
            />
            <select
              value={newItemType}
              onChange={(e) => setNewItemType(e.target.value as 'file' | 'directory')}
              className="text-xs px-2 py-1 border border-gray-300 rounded focus:outline-none"
            >
              <option value="file">File</option>
              <option value="directory">Directory</option>
            </select>
            <button
              onClick={handleCreateNew}
              className="px-2 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
            >
              Create
            </button>
            <button
              onClick={() => {
                setIsCreatingNew(false);
                setNewItemName('');
              }}
              className="px-2 py-1 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      
      {node.type === 'directory' && isExpanded && loadedChildren && (
        <div>
          {loadedChildren.map((child, index) => (
            <TreeNode
              key={`${child.path}-${index}`}
              node={child}
              level={level + 1}
              onFileSelect={onFileSelect}
              onRefresh={onRefresh}
            />
          ))}
        </div>
      )}
      
      {showContextMenu && (
        <div
          data-context-menu
          className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-32"
          style={{
            left: contextMenuPosition.x,
            top: contextMenuPosition.y,
          }}
        >
          {node.type === 'directory' && (
            <>
              <button
                onClick={() => {
                  setIsCreatingNew(true);
                  setNewItemType('file');
                  setShowContextMenu(false);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
              >
                <Plus size={14} />
                <span>New File</span>
              </button>
              <button
                onClick={() => {
                  setIsCreatingNew(true);
                  setNewItemType('directory');
                  setShowContextMenu(false);
                }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center space-x-2"
              >
                <Folder size={14} />
                <span>New Folder</span>
              </button>
              <div className="border-t border-gray-200 my-1"></div>
            </>
          )}
          <button
            onClick={handleDelete}
            className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 text-red-600 flex items-center space-x-2"
          >
            <Trash2 size={14} />
            <span>Delete</span>
          </button>
        </div>
      )}
    </div>
  );
};

const FilesExplorer: React.FC<FilesExplorerProps> = ({ onFileSelect, className = '' }) => {
  const [fileTree, setFileTree] = useState<FileSystemNode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);

  useEffect(() => {
    loadFileTree();
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      const contextMenus = document.querySelectorAll('[data-context-menu]');
      
      // Check if click is outside all context menus
      const isOutsideContextMenu = !Array.from(contextMenus).some(menu => menu.contains(target));
      
      if (isOutsideContextMenu) {
        // Close all context menus by removing them from DOM
        contextMenus.forEach(menu => {
          if (menu instanceof HTMLElement) {
            menu.remove();
          }
        });
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadFileTree = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const webcontainer = WebContainerService.getInstance();
      
      if (!webcontainer.isReady()) {
        await webcontainer.boot();
      }
      
      const tree = await webcontainer.getFileSystemTree();
      setFileTree(tree);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load file tree');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (filePath: string, content: string) => {
    setSelectedFile(filePath);
    onFileSelect?.(filePath, content);
  };

  const handleRefresh = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const webcontainer = WebContainerService.getInstance();
      await webcontainer.refreshFileSystem();
      await loadFileTree();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh file tree');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`h-full flex flex-col ${className}`}>
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">Files</h3>
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-sm text-gray-500">Loading file system...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`h-full flex flex-col ${className}`}>
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-700">Files</h3>
            <button
              onClick={handleRefresh}
              className="text-xs text-blue-500 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-sm text-red-500 text-center">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col ${className}`}>
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-gray-700">Files</h3>
          <button
            onClick={handleRefresh}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Refresh
          </button>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto">
        {fileTree.length === 0 ? (
          <div className="p-4 text-sm text-gray-500">No files found</div>
        ) : (
          <div className="py-2">
            {fileTree.map((node, index) => (
              <TreeNode
                key={`${node.path}-${index}`}
                node={node}
                level={0}
                onFileSelect={handleFileSelect}
                onRefresh={handleRefresh}
              />
            ))}
          </div>
        )}
      </div>
      
      {selectedFile && (
        <div className="p-3 bg-gray-50 border-t border-gray-200">
          <div className="text-xs text-gray-500 truncate">
            Selected: {selectedFile}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesExplorer; 